/**
 * Единый источник актуальных формулировок для всего сайта.
 * Меняй здесь — обновится везде. Нет противоречий между страницами.
 */

/** Год актуальности экзаменов и критериев ФИПИ (ЕГЭ) */
export const EXAM_YEAR = 2026;

/**
 * Старт основной волны ЕГЭ (первый экзаменный день, Москва).
 * Обновляйте по официальному расписанию Рособрезнадзора / ФИПИ на год экзамена (см. EXAM_YEAR).
 */
export const EGE_MAIN_WAVE_FIRST_DAY_ISO = '2026-05-26T09:00:00+03:00';

/** Человекочитаемая подпись для отсчёта */
export const EGE_MAIN_WAVE_COUNTDOWN_TITLE = `До ЕГЭ ${EXAM_YEAR}: основная волна`;

/** Пояснение под таймером */
export const EGE_MAIN_WAVE_COUNTDOWN_NOTE =
  'Дата ориентировочная — уточняется по календарю экзаменов. Отсчёт до 09:00 МСК первого дня основной волны.';

/**
 * Тарифы для репетиторов: оплата за класс, ученики получают доступ по тарифу наставника.
 */
export const EDUCATOR_SUBSCRIPTION_INTRO =
  'Подписка для наставника считается по числу активных привязанных учеников: чем больше класс или группа — тем шире лимиты проверок и аналитики. Ученики по-прежнему могут оформить личный тариф отдельно.';

export const EDUCATOR_SUBSCRIPTION_TIERS = [
  {
    key: 'mentor',
    studentsLabel: 'до 5 учеников',
    priceLabel: 'от 4 490 ₽/мес',
    blurb: 'Индивидуальный формат: письменные проверки, пробники и аналитика по каждому.',
  },
  {
    key: 'studio',
    studentsLabel: 'до 15 учеников',
    priceLabel: 'от 10 490 ₽/мес',
    blurb: 'Мини-группа или несколько параллелей — понятные месячные лимиты на класс.',
  },
  {
    key: 'class',
    studentsLabel: 'до 40 учеников',
    priceLabel: 'от 19 490 ₽/мес',
    blurb: 'Факультатив или класс: расширенные объёмы и отчётность по темам.',
  },
];

/** Нижняя планка в маркетинге (тариф «Плюс») */
export const EDUCATOR_SUBSCRIPTION_FROM_LABEL = 'от 1 390 ₽/мес';

/**
 * Тарифы для родителей: контроль прогресса + спонсорский доступ детям.
 */
export const PARENT_SUBSCRIPTION_INTRO =
  'Тариф родителя открывает кабинет контроля: уведомления, аналитика и еженедельные сводки. Каждый привязанный ребёнок получает «Плюс» или «Максимум» без отдельной оплаты.';

export const PARENT_SUBSCRIPTION_TIERS = [
  {
    key: 'guardian',
    childrenLabel: '1 ребёнок',
    priceLabel: 'от 1 990 ₽/мес',
    blurb: 'Идеально для одного школьника: полный контроль и «Плюс» для него.',
  },
  {
    key: 'family',
    childrenLabel: 'до 3 детей',
    priceLabel: 'от 3 890 ₽/мес',
    blurb: 'Несколько детей или параллельная подготовка к ЕГЭ.',
  },
  {
    key: 'family_max',
    childrenLabel: 'до 5 детей',
    priceLabel: 'от 6 590 ₽/мес',
    blurb: 'Максимальные лимиты проверок и пробников для каждого ребёнка.',
  },
];

export const PARENT_SUBSCRIPTION_FROM_LABEL = 'от 1 990 ₽/мес';

/** Формулировка: в течение какого срока приходят деньги после одобрения возврата */
export const REFUND_MONEY_WITHIN = 'в течение недели';

/** Короткая строка про возврат (хедер, футер, тарифы) */
export const REFUND_LINE_SHORT = `Спокойный возврат: если лимиты тарифа за оплаченный период не использованы — вернём всё, обычно ${REFUND_MONEY_WITHIN}`;

/** Отмена подписки */
export const CANCEL_ANYTIME = 'Подписку можно отменить в один клик, без звонков и писем';

/** Рекуррентные платежи Т-Банка — текст для страницы тарифов */
export function recurringChargeSummary(amountRub, periodMonths) {
  const rub = typeof amountRub === 'number' ? amountRub : 0;
  const months = periodMonths || 1;
  const period =
    months === 1
      ? 'каждый месяц'
      : months === 12
        ? 'раз в 12 месяцев'
        : `каждые ${months} мес.`;
  const formatted = new Intl.NumberFormat('ru-RU').format(rub);
  return `При включённом автопродлении с карты будет списываться ${formatted} ₽ ${period} до отмены подписки.`;
}

export const RECURRING_CONSENT_CHECKBOX =
  'Я согласен(на) на регулярные списания по указанной сумме и периодичности для продления подписки. Согласие даю добровольно, отмечая этот пункт самостоятельно.';

/** Предупреждение: СБП на форме Т-Банка не даёт RebillId для рекуррента */
export const RECURRING_SBP_NOTICE =
  'Если на странице Т-Банка оплатить через СБП, подписка активируется на выбранный срок, но автопродление не сохранится — продлить нужно будет вручную. Для автоматических списаний выберите оплату банковской картой.';

export const RECURRING_CONTACT_LINE =
  'Отменить автопродление или запросить возврат можно в';

/** Тон бренда для футера и общих блоков */
export const BRAND_TAGLINE =
  'РешAI — образовательная платформа для подготовки к ЕГЭ с искусственным интеллектом. Помогаем готовиться системно и видеть результат — ученикам, родителям и репетиторам.';

/**
 * Запасная строка цены, если с API подписки ещё не пришли данные.
 */
export const PRICE_FROM_FALLBACK = '1 390 ₽/мес';

/** За сколько минут приходит проверка развёрнутого ответа */
export const CHECK_TIME = '1 минуту';

/** Количество заданий в банке (для лендинга и блоков про объём) */
export const TASKS_COUNT_LABEL = '1000+';

/** Текстовые блоки для страницы возвратов */
export const REFUND_COPY = {
  possibleTitle: 'Возврат возможен',
  possibleItem:
    'В любой момент по подписке — полная сумма, если месячные лимиты тарифа за оплаченный период не были использованы',
  impossibleAfter: 'Если вы уже пользовались письменными проверками, пробниками или другими платными лимитами тарифа',
  considerDays: '1–2 рабочих дней',
  moneyWithinStep: REFUND_MONEY_WITHIN,
};

/** Лимиты по тарифам — для FAQ и подсказок */
export const PLAN_LIMITS_COPY = {
  plus: { written: 20, mock: 3, explanations: 10, appealsPerDay: 1 },
  max: { written: 100, mock: 12, explanations: 50, appealsPerDay: 3 },
};

/** Реферальная программа — бонусы к квотам, не валюта */
export const REFERRAL_BONUS_COPY = {
  onRegister: '+3 письменные проверки и +2 разбора в этом месяце',
  onFriendPlan: '+5 письменных проверок и +1 пробник в этом месяце',
  intro:
    'Приглашайте одноклассников и друзей — получайте дополнительные лимиты к своему тарифу. Чем больше готовятся вместе, тем выгоднее всем.',
};

/**
 * Краткий дисклеймер в подвале: ИИ-функции без жёстких гарантий исхода.
 */
export const FOOTER_AI_DISCLAIMER =
  'Сервис использует технологии искусственного интеллекта, которые мы постоянно улучшаем. Ответы и автоматические проверки носят справочный, образовательный характер: мы не гарантируем безошибочность, полное соответствие официальным критериям проверки или конкретный результат на экзамене.';

/** Последнее обновление документов (Terms, Privacy, Cookies) */
export const DOCS_LAST_UPDATE = 'Март 2026';

/**
 * Тон обращения: ученикам — «ты», родителям и репетиторам — «вы».
 * Используйте в регистрации, онбординге и кабинетах наставников.
 */
export const REGISTER_COPY = {
  studentRoleLabel: 'Кто ты?',
  educatorRoleLabel: 'Кто вы?',
  studentSubtitle: 'Создайте аккаунт и начните подготовку',
  parentSubtitle: 'Создайте аккаунт родителя и следите за подготовкой ребёнка',
  tutorSubtitle: 'Создайте кабинет репетитора и подключайте учеников',
  parentInviteBanner: 'Приглашение от родителя — подключение после регистрации',
  tutorInviteBanner: 'Приглашение от репетитора — подключение после регистрации',
  parentSignupBlocked:
    'Регистрация родителя сейчас закрыта — вы можете создать аккаунт ученика или дождаться открытия доступа.',
  tutorSignupBlocked:
    'Регистрация репетитора сейчас закрыта — вы можете создать аккаунт ученика или дождаться открытия доступа.',
};
